import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Edit3, Table, Pill, Sparkles, AlertTriangle } from "lucide-react";

export default function PrescriptionTable({ value, onChange, canPrescribeMedicine = true, disabled = false }) {
  const isRestricted = !canPrescribeMedicine || disabled;
  const [mode, setMode] = useState("table"); // "table" | "raw"
  const [medicines, setMedicines] = useState([]);

  const medInputRefs = useRef([]);
  const shouldFocusNewRowRef = useRef(false);
  const addBtnRef = useRef(null);
  const lastEmittedRef = useRef(null);

  // Food timing options
  const foodOptions = [
    "After Food",
    "Before Food",
    "With Food",
    "Empty Stomach",
    "At Bedtime",
    "Anytime"
  ];

  // Quick medicine suggestions for instant click-to-add
  const quickMedSuggestions = [
    "Tab. Paracetamol 650mg",
    "Tab. Pantoprazole 40mg",
    "Tab. Cetirizine 10mg",
    "Cap. Amoxicillin 500mg",
    "Tab. Azithromycin 500mg",
    "Tab. Metformin 500mg"
  ];

  const parseDose = (doseStr) => {
    if (!doseStr || typeof doseStr !== "string") {
      return { morning: true, noon: false, night: true };
    }
    const clean = doseStr.trim();
    if (clean === "SOS") {
      return { morning: false, noon: false, night: false, isSos: true };
    }
    const parts = clean.split("-");
    if (parts.length >= 3) {
      return {
        morning: parts[0] === "1",
        noon: parts[1] === "1",
        night: parts[2] === "1",
        isSos: false
      };
    }
    return { morning: true, noon: false, night: true, isSos: false };
  };

  const getDoseString = (med) => {
    if (med.isSos) return "SOS";
    const m = med.morning ? "1" : "0";
    const a = med.noon ? "1" : "0";
    const n = med.night ? "1" : "0";
    return `${m}-${a}-${n}`;
  };

  // Parse text value into rows on initial load AND whenever it changes from
  // outside this component (e.g. AI-generated prescription overwriting the
  // field). Skip re-parsing when the change is just our own syncToParent
  // echoing back through the parent's state, so it doesn't fight in-progress edits.
  useEffect(() => {
    if (value === lastEmittedRef.current) return;

    if (!value || typeof value !== "string" || value.trim() === "") {
      setMedicines([
        { name: "", food: "After Food", morning: true, noon: false, night: true, duration: "5 Days" }
      ]);
      return;
    }

    const lines = value.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed = [];
    let allStructured = true;

    lines.forEach((line) => {
      const cleanLine = line.replace(/^\d+[\.\)]\s*|^-\s*/, "");
      const parts = cleanLine.split("|").map((p) => p.trim());

      if (parts.length >= 2) {
        const doseInfo = parseDose(parts[2] || "1-0-1");
        parsed.push({
          name: parts[0] || "",
          food: parts[1] || "After Food",
          morning: doseInfo.morning,
          noon: doseInfo.noon,
          night: doseInfo.night,
          isSos: doseInfo.isSos,
          duration: parts[3] || ""
        });
      } else {
        allStructured = false;
      }
    });

    if (parsed.length > 0 && (allStructured || parsed.length === lines.length)) {
      setMedicines(parsed);
    } else {
      setMedicines([
        { name: value.trim(), food: "After Food", morning: true, noon: false, night: true, duration: "" }
      ]);
    }
  }, [value]);

  // Automatically focus medicine name input when a new row is added
  useEffect(() => {
    if (shouldFocusNewRowRef.current) {
      shouldFocusNewRowRef.current = false;
      const lastIndex = medicines.length - 1;
      if (lastIndex >= 0 && medInputRefs.current[lastIndex]) {
        medInputRefs.current[lastIndex].focus();
      }
    }
  }, [medicines.length]);

  // Update parent when medicines change
  const syncToParent = (updatedMeds) => {
    setMedicines(updatedMeds);
    const formatted = updatedMeds
      .filter((m) => m.name.trim() !== "")
      .map((m, idx) => {
        const dose = getDoseString(m);
        const parts = [
          `${idx + 1}. ${m.name.trim()}`,
          m.food || "After Food",
          dose
        ];
        if (m.duration && m.duration.trim()) {
          parts.push(m.duration.trim());
        }
        return parts.join(" | ");
      })
      .join("\n");
    lastEmittedRef.current = formatted;
    onChange({ target: { name: "gp_prescription", value: formatted } });
  };

  const handleRowChange = (index, field, val) => {
    const next = [...medicines];
    next[index] = { ...next[index], [field]: val };
    syncToParent(next);
  };

  const handleCheckboxChange = (index, slot, checked) => {
    const next = [...medicines];
    next[index] = { ...next[index], [slot]: checked, isSos: false };
    syncToParent(next);
  };

  const handleToggleSos = (index) => {
    const next = [...medicines];
    const currentSos = !!next[index].isSos;
    if (!currentSos) {
      next[index] = { ...next[index], morning: false, noon: false, night: false, isSos: true };
    } else {
      next[index] = { ...next[index], morning: true, noon: false, night: true, isSos: false };
    }
    syncToParent(next);
  };

  const handleAddRow = (prefillName = "") => {
    shouldFocusNewRowRef.current = true;
    // If only one row and it's empty, replace it with prefillName
    if (medicines.length === 1 && medicines[0].name.trim() === "" && prefillName) {
      const next = [{ ...medicines[0], name: prefillName }];
      syncToParent(next);
      return;
    }
    const next = [
      ...medicines,
      { name: prefillName, food: "After Food", morning: true, noon: false, night: true, duration: "5 Days" }
    ];
    syncToParent(next);
  };

  const handleRemoveRow = (index) => {
    if (medicines.length <= 1) {
      syncToParent([{ name: "", food: "After Food", morning: true, noon: false, night: true, duration: "" }]);
      return;
    }
    const next = medicines.filter((_, i) => i !== index);
    syncToParent(next);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-3.5 shadow-2xs">
      {/* Compact Header with Rx Badge, Print Button & View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-2.5 mb-2.5">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 shrink-0 select-none">
            <span className="font-serif italic text-xl font-black leading-none">℞</span>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-bold text-gray-900 leading-none">
                Rx Prescription
              </h3>
              <span className="text-[10px] font-semibold bg-blue-100/70 text-blue-700 px-1.5 py-0.2 rounded border border-blue-200/60">
                Doctor's Orders
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
              Compact single-line entry. Select meal timing, dose checkboxes, and duration.
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
          <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200">
            <button
              type="button"
              onClick={() => setMode("table")}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors flex items-center gap-1 cursor-pointer ${mode === "table" ? "bg-white text-gray-900 shadow-2xs" : "text-gray-500 hover:text-gray-800"
                }`}
            >
              <Table className="w-3 h-3 text-blue-600" />
              Table
            </button>
            <button
              type="button"
              onClick={() => setMode("raw")}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors flex items-center gap-1 cursor-pointer ${mode === "raw" ? "bg-white text-gray-900 shadow-2xs" : "text-gray-500 hover:text-gray-800"
                }`}
            >
              <Edit3 className="w-3 h-3 text-gray-600" />
              Free Text
            </button>
          </div>
        </div>
      </div>

      {isRestricted && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 text-xs flex items-start gap-2.5 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-bold">Prescription Restricted:</span> This doctor's specialization (e.g. Clinical Psychologist, Psychotherapist) does not permit prescribing medicines. You can still record advice, session notes, and clinical observations.
          </div>
        </div>
      )}

      {mode === "table" ? (
        <div className="space-y-2.5">
          {/* Quick suggestions bar - compact */}
          {/* <div className="flex items-center gap-1 flex-wrap text-xs text-gray-500">
            <span className="text-[10px] font-semibold text-gray-500 flex items-center gap-0.5 mr-0.5">
              <Sparkles className="w-2.5 h-2.5 text-blue-500" /> Quick Add:
            </span>
            {quickMedSuggestions.map((med) => (
              <button
                key={med}
                type="button"
                onClick={() => handleAddRow(med)}
                className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700 border border-gray-200 hover:border-blue-200 rounded transition-colors cursor-pointer"
              >
                + {med}
              </button>
            ))}
          </div> */}

          {/* Compact High-Density Table */}
          <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            <table className="w-full text-left border-collapse min-w-[660px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/90 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                  <th className="py-1.5 px-2 w-8 text-center">#</th>
                  <th className="py-1.5 px-2 min-w-[200px]">
                    <div className="flex items-center gap-1">
                      <Pill className="w-3 h-3 text-blue-500" />
                      Medicine Name & Strength
                    </div>
                  </th>
                  <th className="py-1.5 px-2 w-36">Timing (Food)</th>
                  <th className="py-1.5 px-2 w-52">Dose (M - A - N)</th>
                  <th className="py-1.5 px-2 w-28">Duration</th>
                  <th className="py-1.5 px-1.5 w-8 text-center">Del</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {medicines.map((med, idx) => (
                  <tr key={idx} className="hover:bg-blue-50/20 transition-colors group">
                    {/* Index */}
                    <td className="py-1 px-2 text-center align-middle">
                      <span className="text-xs font-semibold text-gray-400 select-none">
                        {idx + 1}
                      </span>
                    </td>

                    {/* Column 1: Medicine Name (Auto-focused on add) */}
                    <td className="py-1 px-2 align-middle">
                      <input
                        ref={(el) => (medInputRefs.current[idx] = el)}
                        type="text"
                        value={med.name}
                        onChange={(e) => handleRowChange(idx, "name", e.target.value)}
                        placeholder="e.g. Tab. Paracetamol 650mg"
                        className="w-full h-8 text-xs font-medium text-gray-900 border border-gray-300 rounded-md px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-400 transition-colors"
                      />
                    </td>

                    {/* Column 2: Food Timing (Auto-opens on focus/cursor) */}
                    <td className="py-1 px-2 align-middle">
                      <select
                        value={med.food}
                        onChange={(e) => handleRowChange(idx, "food", e.target.value)}
                        onFocus={(e) => {
                          try {
                            if (typeof e.target.showPicker === "function") {
                              e.target.showPicker();
                            }
                          } catch (err) { }
                        }}
                        className="w-full h-8 text-xs text-gray-800 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer transition-colors"
                      >
                        {foodOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Column 3: 3 Checkboxes (M - A - N) + Badge (Single Line) */}
                    <td className="py-1 px-2 align-middle">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-2 py-1 rounded-md">
                          <label
                            className="inline-flex items-center gap-1 cursor-pointer text-[11px] font-bold text-gray-700 select-none hover:text-blue-600"
                            title="Morning dose"
                          >
                            <input
                              type="checkbox"
                              checked={!!med.morning}
                              onChange={(e) => handleCheckboxChange(idx, "morning", e.target.checked)}
                              className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer accent-blue-600"
                            />
                            <span>M</span>
                          </label>

                          <span className="text-gray-300 text-[10px]">|</span>

                          <label
                            className="inline-flex items-center gap-1 cursor-pointer text-[11px] font-bold text-gray-700 select-none hover:text-blue-600"
                            title="Afternoon / Noon dose"
                          >
                            <input
                              type="checkbox"
                              checked={!!med.noon}
                              onChange={(e) => handleCheckboxChange(idx, "noon", e.target.checked)}
                              className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer accent-blue-600"
                            />
                            <span>A</span>
                          </label>

                          <span className="text-gray-300 text-[10px]">|</span>

                          <label
                            className="inline-flex items-center gap-1 cursor-pointer text-[11px] font-bold text-gray-700 select-none hover:text-blue-600"
                            title="Night dose"
                          >
                            <input
                              type="checkbox"
                              checked={!!med.night}
                              onChange={(e) => handleCheckboxChange(idx, "night", e.target.checked)}
                              className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer accent-blue-600"
                            />
                            <span>N</span>
                          </label>
                        </div>

                        {/* Calculated Dose Badge (click to toggle SOS) */}
                        <button
                          type="button"
                          onClick={() => handleToggleSos(idx)}
                          title="Click to toggle SOS / as needed"
                          className={`font-mono text-[11px] font-bold px-1.5 py-0.5 rounded border transition-colors cursor-pointer select-none ${med.isSos
                              ? "bg-amber-100 text-amber-800 border-amber-300"
                              : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                            }`}
                        >
                          {getDoseString(med)}
                        </button>
                      </div>
                    </td>

                    {/* Column 4: Dedicated Duration Column (Tab jumps to Add Row) */}
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="text"
                        value={med.duration || ""}
                        onChange={(e) => handleRowChange(idx, "duration", e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Tab" && !e.shiftKey) {
                            e.preventDefault();
                            addBtnRef.current?.focus();
                          }
                        }}
                        placeholder="5 Days"
                        className="w-full h-8 text-xs text-gray-800 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder:text-gray-400 transition-colors"
                        title="Duration (e.g. 5 Days, 1 Week). Press Tab to jump to + Add Row"
                      />
                    </td>

                    {/* Column 5: Delete Action */}
                    <td className="py-1 px-1.5 text-center align-middle">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                        title="Remove medicine"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Medicine Row Button */}
          <div className="pt-0.5">
            <button
              ref={addBtnRef}
              type="button"
              onClick={() => handleAddRow()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg font-semibold text-xs transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-blue-100"
            >
              <Plus className="w-3.5 h-3.5 text-blue-600" />
              Add Medicine Row
            </button>
          </div>
        </div>
      ) : (
        /* Raw Text Area fallback */
        <div className="space-y-2">
          <label className="text-xs font-semibold text-gray-600">
            Free-Text Prescription (Formatted for Report/Print):
          </label>
          <textarea
            name="gp_prescription"
            value={value || ""}
            onChange={onChange}
            placeholder="Type medicines or instructions freely here..."
            className="w-full min-h-[140px] p-2.5 text-xs font-mono text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
          />
          <p className="text-[11px] text-gray-400">
            Tip: You can switch back to Table View anytime. Each line is stored as: Medicine | Food Timing | Dose | Duration
          </p>
        </div>
      )}
    </div>
  );
}
